import axios, { AxiosError, AxiosRequestConfig, CancelTokenSource } from 'axios'
import CONFIG from 'config/constants'

const AxiosHeaders = axios.AxiosHeaders
import { getAccessToken, refreshAccessToken } from 'utils/tokenRefresh'

type RetryAxiosRequestConfig = AxiosRequestConfig & {
  _retry?: boolean
}

axios.defaults.headers.common['Content-Type'] = 'application/json'

const cancelSources: CancelTokenSource[] = []

axios.interceptors.request.use(
  (config) => {
    const cfg = config
    const requestUrl = cfg.url || ''
    const isLogin = requestUrl.includes('/login')
    const token = getAccessToken()

    if (cfg.headers && !isLogin) {
      const headers = AxiosHeaders.from(cfg.headers)
      if (token) {
        headers.set('Authorization', `Bearer ${token}`)
      }
      headers.set('X-API-Key', CONFIG.API_KEY)
      cfg.headers = headers
    }

    // Let the browser set the multipart boundary. A manual Content-Type
    // without that boundary makes logo uploads fail.
    if (typeof FormData !== 'undefined' && cfg.data instanceof FormData && cfg.headers) {
      const headers = AxiosHeaders.from(cfg.headers)
      headers.set('Content-Type', false)
      cfg.headers = headers
    }

    const cs = axios.CancelToken.source()
    cancelSources.push(cs)

    cfg.cancelToken = cs.token

    return cfg
  },
  (error) => Promise.reject(error)
)

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (axios.isCancel(error)) {
      return Promise.reject(error)
    }

    const status = error.response?.status
    const originalRequest = error.config as RetryAxiosRequestConfig | undefined
    const requestUrl = originalRequest?.url || ''
    const isAuthEndpoint =
      requestUrl.includes('/login') || requestUrl.includes('/refresh')

    if (
      status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !isAuthEndpoint
    ) {
      originalRequest._retry = true

      const newToken = await refreshAccessToken()
      if (newToken) {
        const headers = AxiosHeaders.from(originalRequest.headers)
        headers.set('Authorization', `Bearer ${newToken}`)
        originalRequest.headers = headers
        return axios(originalRequest)
      }
    }

    return Promise.reject(error.response?.data ?? error)
  }
)

function cancelAll() {
  cancelSources.forEach((source) => source.cancel('Request cancelled'))
}

function isCancel(error: AxiosError) {
  return axios.isCancel(error)
}

function get<T>(url: string, config?: AxiosRequestConfig) {
  const res = axios.get<T>(url, config)
  return res
}

function post<T>(url: string, data: object, config?: AxiosRequestConfig) {
  const res = axios.post<T>(url, data, config)

  return res
}

function request(config: AxiosRequestConfig) {
  const res = axios(config)

  return res
}

const http = { get, post, cancelAll, isCancel, request }

export default http
